import type { ParsedUrlQuery } from 'querystring';

import type { SearchOdoscopeParametersInput } from '@packages/gql/generated/shopping/graphql';
import config from '@packages/config';
import {
  ConsentCategory,
  getCookieConsent,
} from '@packages/shared/src/utils/getCookieConsent/getCookieConsent';

import { ODOSCOPE_PARAMS_COOKIE_NAME, type OdoscopeAttributionData } from './odoscopeAttribution';
import { maskIpAddress } from './maskIpAddress';

/**
 * Get Odoscope parameters from cookies and optional server-side headers/query.
 * @param cookies - Cookie object
 * @param query - Optional parsed URL query (for first SSR load)
 * @param serverContext - Optional server-side context (IP, User-Agent, Referrer)
 */
export const getOdoscopeParams = (
  cookies: Record<string, string>,
  query?: ParsedUrlQuery,
  serverContext?: { clientIp?: string; userAgent?: string; referrer?: string },
): SearchOdoscopeParametersInput | undefined => {
  const {
    search: { odoscopeToken },
  } = config;

  if (!getCookieConsent(cookies, ConsentCategory.Marketing) || !odoscopeToken) {
    return undefined;
  }

  const OSC_COOKIE_NAME = `osc-${odoscopeToken}`;

  let odoscopeData: OdoscopeAttributionData | undefined;
  if (cookies[ODOSCOPE_PARAMS_COOKIE_NAME]) {
    try {
      odoscopeData = JSON.parse(cookies[ODOSCOPE_PARAMS_COOKIE_NAME]) as OdoscopeAttributionData;
    } catch {
      // Invalid JSON in cookie - ignore and continue without odoscope data
    }
  }

  // Fallback to query params for first-load SSR if cookie is missing
  if (!odoscopeData && query) {
    const utmSource = typeof query.utm_source === 'string' ? query.utm_source : undefined;
    const utmMedium = typeof query.utm_medium === 'string' ? query.utm_medium : undefined;
    const utmCampaign = typeof query.utm_campaign === 'string' ? query.utm_campaign : undefined;

    if (utmSource || utmMedium || utmCampaign) {
      odoscopeData = { utmSource, utmMedium, utmCampaign };
    }
  }

  // Odoscope cookie
  const oscCookie = cookies[OSC_COOKIE_NAME];

  // Headers server context
  const { clientIp, userAgent, referrer } = serverContext ?? {};

  // Odoscope data (captured from cookie or query)
  const { clientReferrer, utmMedium, utmSource, utmCampaign } = odoscopeData ?? {};

  return {
    // Mandatory fields for SearchOdoscopeParametersInput
    oscCookie: oscCookie ?? '',
    userAgent: userAgent ?? '',
    clientIp: maskIpAddress(clientIp) ?? '',
    clientReferrer: clientReferrer ?? referrer ?? '',
    // Optional UTM parameters (InputMaybe)
    ...(utmMedium && { cpUtmMedium: utmMedium }),
    ...(utmCampaign && { cpUtmCampaign: utmCampaign }),
    ...(utmSource && { cpUtmSource: utmSource }),
  };
};
